import { makeSkillWebAuthorizeRedirectUrl } from '@/main/factories/http'
import { AxiosHttpAuthClient } from '../axios-http-auth-client'
import {
  ApiInstanceRequest,
  ApiInstanceResponse,
  ApiInstanceReturn,
  ErrorFactory
} from './http-service-client-types'
import { HttpStatusCode } from '@/data/protocols/http'
import {
  InvalidCredentialsError,
  ServerError,
  ForbiddenError,
  NotFoundError,
  ConflictError,
  BadRequestError
} from '@/domain/errors'

const errorMap: Record<number, ErrorFactory> = {
  [HttpStatusCode.badRequest]: msg => new BadRequestError(msg),
  [HttpStatusCode.forbidden]: msg => new ForbiddenError(msg),
  [HttpStatusCode.unauthorized]: msg => new InvalidCredentialsError(msg),
  [HttpStatusCode.notFound]: msg => new NotFoundError(msg),
  [HttpStatusCode.conflict]: msg => new ConflictError(msg),
  [HttpStatusCode.serverError]: msg => new ServerError(msg)
}

/**
 * Handles errors based on the API response status code and body.
 *
 * - If the response status code matches an entry in `errorMap`, throws the corresponding error with the response message.
 * - If the response status code indicates success but the response body indicates failure, throws a bad request error.
 * - Otherwise, does nothing.
 *
 * @template T The type of the response body.
 * @param response The API response object containing status code and body.
 * @throws Throws an error if the response status code or body indicates a failure.
 */
const handleError = <T>(response: ApiInstanceReturn<T>): never | void => {
  const errorFactory = errorMap[response.statusCode]
  if (errorFactory) {
    throw Object.assign(errorFactory(response.body?.message), { statusCode: response.statusCode })
  }

  const isSuccessStatus =
    response.statusCode >= HttpStatusCode.ok && response.statusCode < HttpStatusCode.multipleChoices

  if (isSuccessStatus && !response.body.success) {
    throw Object.assign(errorMap[HttpStatusCode.badRequest](response.body?.message), {
      statusCode: response.statusCode
    })
  }
}

/**
 * Creates an instance of `AxiosHttpAuthClient` configured to handle authentication redirects.
 *
 * When authentication is required, the client will redirect the browser to the authorization URL
 * generated by `makeSkillWebAuthorizeRedirectUrl()`.
 *
 * @returns {AxiosHttpAuthClient} An authenticated HTTP client for making requests.
 */
const createAuthClient = (): AxiosHttpAuthClient => {
  return new AxiosHttpAuthClient(() => {
    window.location.href = makeSkillWebAuthorizeRedirectUrl()
  })
}

/**
 * A record to keep track of active AbortControllers for each URL.
 * This allows for aborting previous requests to the same URL when a new request is made.
 */
const controllers = new Map<string, AbortController>()

/**
 * Makes an HTTP request using an authenticated Axios client and returns a standardized response.
 *
 * @template T - The expected type of the response body.
 * @param {ApiInstanceRequest} config - The configuration object for the HTTP request.
 * @returns {Promise<ApiInstanceReturn<T>>} - A promise that resolves to the standardized API response.
 *
 * @remarks
 * This function creates an authenticated Axios client, performs the request, handles any errors,
 * and returns the response in a consistent format.
 */
export async function httpServiceClient<T>(
  config: ApiInstanceRequest
): Promise<ApiInstanceReturn<T>> {
  const axiosHttpAuthClient = createAuthClient()
  const abortController = new AbortController()

  if (controllers.has(config.url)) {
    controllers.get(config.url)!.abort()
  }

  controllers.set(config.url, abortController)

  const apiResponse = await axiosHttpAuthClient.request({
    ...config,
    signal: abortController.signal
  })

  const response: ApiInstanceReturn<T> = {
    statusCode: apiResponse.statusCode,
    headers: apiResponse.headers,
    body: apiResponse.body as ApiInstanceResponse<T>
  }

  handleError(response)

  return response
}
